import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Spinner from "src/components/common/Spinner";
import { ManageComboModal } from "src/components/ManageComboModal/ManageComboModal";
import ComboCard from "src/components/ComboCard/ComboCard";
import SearchBar from "src/components/common/SearchBar";
import {
  delete_combo_modal,
  search_combo,
  set_combo,
  set_combo_loding,
} from "src/store/features/combo-slice";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import { GET_COMBOS } from "src/constants/queries";
import { useAuth } from "src/contexts/auth-provider";
import { restaurantSegment } from "src/utils/restaurantUrl";
import { MENU_LIST_FILTER_TABS, type MenuListActiveFilter } from "src/constants/menuListActiveFilter";

const Combo = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isComboLoader, setIsComboLoader] = useState(false);
  const { axBe } = useAxiosInterceptor();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { filteredCombo } = useSelector((state: any) => state.combo);
  const { hasRestaurant } = useAuth();
  const [activeFilter, setActiveFilter] = useState<MenuListActiveFilter>("ACTIVE");
  const searchQueryRef = useRef("");

  const handleComboClick = (comboId: any) => {
    dispatch(delete_combo_modal(comboId));
  };

  const getCombos = useCallback(async () => {
    if (!hasRestaurant?.correlationId) return;
    dispatch(set_combo_loding(true));
    setIsComboLoader(true);
    try {
      const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: GET_COMBOS,
        variables: {
          storeCorrelationId: hasRestaurant.correlationId,
          activeFilter,
        },
      });
      dispatch(
        set_combo({
          combo: data?.data?.combosByStoreCorrelationId || [],
        })
      );
      dispatch(search_combo(searchQueryRef.current));
    } catch (error) {
      console.error(error);
    } finally {
      setTimeout(() => {
        setIsComboLoader(false);
        dispatch(set_combo_loding(false));
      }, 300);
    }
  }, [axBe, dispatch, hasRestaurant?.correlationId, activeFilter]);

  useEffect(() => {
    getCombos();
  }, [getCombos]);

  const segment = restaurantSegment(hasRestaurant?.correlationId ?? "", hasRestaurant?.name);
  const navigateTo = (combo: any) => {
    navigate(`/restaurants/${segment}/combo/${combo?.correlationId}`, { state: { comboData: combo } });
  };

  const searchCombo = (value: string) => {
    searchQueryRef.current = value;
    dispatch(search_combo(value));
  };

  const handleEditCombo = (combo: any) => {
    navigate(`/restaurants/${segment}/combo/${combo?.correlationId}`, { state: { comboData: combo } });
  };

  return (
    <div className="flex w-full min-w-0 shrink-0 flex-col pb-4">
      <h1 className="text-[20px] font-semibold text-[#111] mb-1" style={{ fontWeight: 600 }}>
        Combos
      </h1>
      <p className="text-xs text-[#6B7280] mb-4">Create and manage combo deals.</p>

      <div className="flex w-full min-w-0 shrink-0 flex-col gap-3 rounded-lg border border-[#E5E7EB] bg-white px-3 py-2 mb-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <div className="w-full min-w-0 sm:flex-1 sm:max-w-[260px]">
          <SearchBar placeholder="Search Combo..." onChange={searchCombo} />
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto sm:justify-end sm:shrink-0">
          <div
            className="flex flex-wrap items-center gap-1 rounded-lg border border-[#F1F1F2] p-0.5 bg-white w-full sm:w-fit"
            role="group"
            aria-label="Filter combos by status"
          >
            {MENU_LIST_FILTER_TABS.map(({ id, label }) => {
              const selected = activeFilter === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setActiveFilter(id)}
                  className={`h-[38px] px-3 text-xs font-medium rounded-md transition-all outline-none focus-visible:ring-2 focus-visible:ring-[#FD7657]/40 flex-1 sm:flex-none min-w-0 ${
                    selected
                      ? "bg-[#FD7657] text-white shadow-sm"
                      : "bg-white text-[#6b7280] hover:bg-[#FEF5F4] hover:text-[#FD7657]"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            className="h-8 px-4 rounded-lg text-sm font-medium text-white bg-[#FD7657] hover:bg-[#e45e41] shrink-0 transition-colors w-full sm:w-auto"
            onClick={() => setShowCreateModal(true)}
          >
            Add Combo
          </button>
        </div>
      </div>

      <div className="w-full min-w-0">
        {isComboLoader ? (
          <div className="flex justify-center w-full py-12">
            <Spinner />
          </div>
        ) : filteredCombo && filteredCombo.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-y-4 gap-x-6">
            {filteredCombo.map((combo: any, index: number) => (
              <ComboCard
                key={combo?.correlationId ?? index}
                combo={combo}
                restaurantCorrelationId={hasRestaurant?.correlationId ?? ""}
                onEdit={handleEditCombo}
                onDelete={handleComboClick}
                onCardClick={navigateTo}
              />
            ))}
          </div>
        ) : (
          <p className="text-[#6b7280] py-12 text-center text-sm">
            No combos yet. Click &quot;Add Combo&quot; to create your first one.
          </p>
        )}
      </div>

      {showCreateModal && (
        <ManageComboModal
          title="Create Combo"
          setShowCreateModal={() => setShowCreateModal(false)}
          getCombos={getCombos}
        />
      )}
    </div>
  );
};

export default Combo;
